import React, { useEffect, useState, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLeaf, faMoon, faBars, faTimes } from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import './HomePage.css';

// Custom Sun Component
const CustomSunIcon = ({ className, style }) => (
  <svg 
    className={className}
    style={style}
    width="1.6rem" 
    height="1.6rem" 
    viewBox="0 0 24 24" 
    fill="currentColor"
  >
    {/* Central sun circle */}
    <circle cx="12" cy="12" r="5" fill="currentColor"/>
    
    {/* Sun rays */}
    <g stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      {/* Vertical and horizontal rays */}
      <line x1="12" y1="1" x2="12" y2="3"/>
      <line x1="12" y1="21" x2="12" y2="23"/>
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
      <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
      <line x1="1" y1="12" x2="3" y2="12"/>
      <line x1="21" y1="12" x2="23" y2="12"/>
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
      <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
    </g>
  </svg>
);
const HomePage = () => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme ? savedTheme === 'dark' : true;
  });
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const cursorRef = useRef(null);
  const cursorDotRef = useRef(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    document.body.className = isDarkMode ? 'dark-theme' : 'light-theme';
  }, [isDarkMode]);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const onMouseMove = (e) => {
      // Only track cursor on non-touch devices
      if (windowWidth > 768) {
        setPosition({ x: e.clientX, y: e.clientY });
        
        if (cursorRef.current) {
          cursorRef.current.style.left = `${e.clientX}px`;
          cursorRef.current.style.top = `${e.clientY}px`;
        }
        
        if (cursorDotRef.current) {
          cursorDotRef.current.style.left = `${e.clientX}px`;
          cursorDotRef.current.style.top = `${e.clientY}px`;
        }
      }
    };

    window.addEventListener('mousemove', onMouseMove);

    return () => {
      window.removeEventListener('mousemove', onMouseMove);
    };
  }, [windowWidth]);

  useEffect(() => {
    const createParticles = () => {
      const particleCount = windowWidth > 768 ? 50 : 20; // Fewer particles on mobile
      const existingParticles = document.querySelectorAll('.particle');
      existingParticles.forEach(particle => particle.remove());
      
      for (let i = 0; i < particleCount; i++) {
        const particle = document.createElement('div');
        particle.className = 'particle';
        particle.style.width = `${Math.random() * (windowWidth > 768 ? 5 : 3)}px`; // Smaller particles on mobile
        particle.style.height = particle.style.width;
        particle.style.left = `${Math.random() * 100}vw`;
        particle.style.top = `${Math.random() * 100}vh`;
        document.body.appendChild(particle);
      }
    };

    createParticles();
    
    // Re-create particles on window resize
    const handleResize = () => {
      createParticles();
    };
    
    window.addEventListener('resize', handleResize);

    return () => {
      const existingParticles = document.querySelectorAll('.particle');
      existingParticles.forEach(particle => particle.remove());
      window.removeEventListener('resize', handleResize);
    };
  }, [isDarkMode, windowWidth]);

  const toggleTheme = () => {
    setIsDarkMode(prevMode => {
      const newMode = !prevMode;
      localStorage.setItem('theme', newMode ? 'dark' : 'light');
      return newMode;
    });
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const themeClass = isDarkMode ? 'dark-theme' : 'light-theme';

  const styles = {
    // Responsive nav
    desktopNav: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: windowWidth <= 768 ? '10px' : '20px',
      position: 'relative',
      zIndex: 100,
      flexWrap: 'nowrap',
      backgroundColor: isDarkMode ? 'rgba(0, 0, 0, 0.8)' : 'var(--light-pale-green)',
      borderBottom: `1px solid ${isDarkMode ? 'var(--dark-primary-green)' : 'var(--light-light-green)'}`,
    },
    logoSection: {
      display: 'flex',
      alignItems: 'center',
      fontSize: windowWidth <= 480 ? '0.89rem' : 'clamp(1rem, 3vw, 1.5rem)',
      color: isDarkMode ? 'var(--dark-primary-green)' : 'var(--light-dark-green)',
      textDecoration: 'none',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      maxWidth: windowWidth <= 480 ? '200px' : 'none',
    },
    logoText: {
      display: 'inline',
      fontSize: windowWidth <= 360 ? '0.8rem' : 'inherit',
    },
    logoIcon: {
      marginRight: '8px',
      display: 'inline-block',
    },
    navControls: {
      display: 'flex',
      alignItems: 'center',
      gap: windowWidth <= 480 ? '5px' : '10px',
      flexShrink: 0,
    },
    desktopNavLinks: {
      display: windowWidth <= 768 ? 'none' : 'flex',
      gap: '20px',
    },
    navLink: {
      color: isDarkMode ? 'var(--dark-primary-green)' : 'var(--light-dark-green)',
      textDecoration: 'none',
    },
    themeToggle: {
      background: 'none',
      border: 'none',
      fontSize: '1.2rem',
      color: isDarkMode ? 'var(--dark-primary-green)' : 'var(--light-dark-green)',
      cursor: 'pointer',
      padding: '5px',
      marginRight: '5px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    hamburger: {
      display: windowWidth <= 768 ? 'block' : 'none',
      background: 'none',
      border: 'none',
      fontSize: '1.5rem',
      color: isDarkMode ? 'var(--dark-primary-green)' : 'var(--light-dark-green)',
      cursor: 'pointer',
      padding: '5px',
    },
    mobileMenu: {
      display: mobileMenuOpen ? 'flex' : 'none',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: isDarkMode ? 'rgba(0, 0, 0, 0.95)' : 'rgba(255, 255, 255, 0.95)',
      zIndex: 1000,
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
    },
    mobileMenuClose: {
      position: 'absolute',
      top: '20px',
      right: '20px',
      background: 'none',
      border: 'none',
      fontSize: '1.5rem',
      color: isDarkMode ? 'var(--dark-primary-green)' : 'var(--light-dark-green)',
      cursor: 'pointer',
    },
    mobileNavLinks: {
      display: 'flex',
      flexDirection: 'column',
      gap: '20px',
      fontSize: '1.5rem',
    },
    mobileNavLink: {
      color: isDarkMode ? 'var(--dark-primary-green)' : 'var(--light-dark-green)',
      textDecoration: 'none',
      padding: '10px',
    },
    // Main content responsive styles
    maintext: {
      textAlign: 'center',
      padding: windowWidth <= 768 ? '60px 10px' : '100px 20px',
      perspective: '1000px',
    },
    mainTitle: {
      fontSize: windowWidth <= 768 ? '3rem' : '4rem',
      margin: 0,
      lineHeight: 1.2,
      cursor: 'pointer',
      transition: 'transform 0.3s ease',
      background: isDarkMode 
        ? 'linear-gradient(135deg, var(--dark-primary-green), var(--dark-light-green))'
        : 'none',
      backgroundClip: isDarkMode ? 'text' : 'none',
      WebkitBackgroundClip: isDarkMode ? 'text' : 'none',
      WebkitTextFillColor: isDarkMode ? 'transparent' : 'var(--light-dark-green)',
      color: isDarkMode ? 'transparent' : 'var(--light-dark-green)',
    },
    splitContainer: {
      display: 'flex',
      flexDirection: windowWidth <= 768 ? 'column' : 'row',
      justifyContent: 'center',
      gap: windowWidth <= 768 ? '20px' : '40px',
      padding: '30px 20px',
      maxWidth: '1200px',
      margin: '0 auto',
      perspective: '1000px',
    },
    sectionCard: {
      flex: 1,
      borderRadius: '15px',
      padding: windowWidth <= 768 ? '20px' : '30px',
      transition: 'all 0.5s ease',
      transformStyle: 'preserve-3d',
      backgroundColor: isDarkMode ? 'rgba(46, 204, 113, 0.1)' : 'rgba(46, 204, 113, 0.1)',
      border: `1px solid ${isDarkMode ? 'var(--dark-primary-green)' : 'var(--light-light-green)'}`,
    },
    cardTitle: {
      fontSize: windowWidth <= 768 ? '1.8rem' : '2.2rem',
      margin: '0 0 20px',
      position: 'relative',
      display: 'inline-block',
      transition: 'transform 0.3s ease',
      color: isDarkMode ? 'var(--dark-primary-green)' : 'var(--light-green)',
    },
    cardText: {
      fontSize: windowWidth <= 768 ? '1rem' : '1.1rem',
      lineHeight: 1.8,
      transition: 'transform 0.3s ease',
      color: isDarkMode ? '#e0e0e0' : 'var(--light-dark-green)',
    }
  };

  return (
    <div className={themeClass}>
      {windowWidth > 768 && (
        <>
          <div ref={cursorRef} className="custom-cursor" />
          <div ref={cursorDotRef} className="cursor-dot" />
        </>
      )}
      
      <div className="bg-overlay" />
      
      <header className={themeClass}>
        <h1>Sustainable minds on the sustainable grind</h1>
      </header>

      {/* Desktop Navigation */}
      <nav style={styles.desktopNav}>
        <Link to="/" style={styles.logoSection}>
          <FontAwesomeIcon icon={faLeaf} style={styles.logoIcon} />
          <span style={styles.logoText}>The Sustainable Formula</span>
        </Link>
        
        <div style={styles.navControls}>
          <div style={styles.desktopNavLinks}>
            <Link to="/team" style={styles.navLink}>Our Team</Link>
            <Link to="/about" style={styles.navLink}>About Us</Link>
            <Link to="/initiatives" style={styles.navLink}>Initiatives</Link>
            <Link to="/donate" style={styles.navLink}>Donate</Link>
          </div>
          
          <button 
            style={styles.themeToggle}
            onClick={toggleTheme}
            aria-label={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
          >
            {isDarkMode ? (
              <CustomSunIcon />
            ) : (
              <FontAwesomeIcon icon={faMoon} />
            )}
          </button>
          
          <button 
            style={styles.hamburger} 
            onClick={toggleMobileMenu}
            aria-label="Menu"
          >
            <FontAwesomeIcon icon={faBars} />
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      <div style={styles.mobileMenu}>
        <button 
          style={styles.mobileMenuClose} 
          onClick={toggleMobileMenu}
          aria-label="Close menu"
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
        <div style={styles.mobileNavLinks}>
          <Link 
            to="/team" 
            style={styles.mobileNavLink}
            onClick={() => setMobileMenuOpen(false)}
          >
            Our Team
          </Link>
          <Link 
            to="/about" 
            style={styles.mobileNavLink}
            onClick={() => setMobileMenuOpen(false)}
          >
            About Us
          </Link>
          <Link 
            to="/initiatives" 
            style={styles.mobileNavLink}
            onClick={() => setMobileMenuOpen(false)}
          >
            Initiatives
          </Link>
          <Link 
            to="/donate" 
            style={styles.mobileNavLink}
            onClick={() => setMobileMenuOpen(false)}
          >
            Donate
          </Link>
        </div>
      </div>

      <section style={styles.maintext}>
        <h2 style={styles.mainTitle}>The</h2>
        <h2 style={styles.mainTitle}>sustainable</h2>
        <h2 style={styles.mainTitle}>formula.</h2>
      </section>

      <div style={styles.splitContainer}>
        <section 
          style={styles.sectionCard}
          onMouseEnter={(e) => {
            if (windowWidth > 768) {
              e.currentTarget.style.transform = 'translateZ(50px)';
              e.currentTarget.style.boxShadow = isDarkMode ? 
                '20px 20px 60px rgba(46, 204, 113, 0.5), -20px -20px 60px rgba(46, 204, 113, 0.5)' : 
                '20px 20px 60px rgba(94, 171, 112, 0.3), -20px -20px 60px rgba(94, 171, 112, 0.3)';
            }
          }}
          onMouseLeave={(e) => {
            if (windowWidth > 768) {
              e.currentTarget.style.transform = 'translateZ(0)';
              e.currentTarget.style.boxShadow = 'none';
            }
          }}
          onTouchStart={(e) => {
            if (windowWidth <= 768) {
              e.currentTarget.style.transform = 'translateY(-5px)';
              e.currentTarget.style.boxShadow = isDarkMode ? 
                '10px 10px 30px rgba(46, 204, 113, 0.3), -10px -10px 30px rgba(46, 204, 113, 0.3)' : 
                '10px 10px 30px rgba(94, 171, 112, 0.2), -10px -10px 30px rgba(94, 171, 112, 0.2)';
            }
          }}
          onTouchEnd={(e) => {
            if (windowWidth <= 768) {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }
          }}
        >
          <h2 style={styles.cardTitle}>About Us</h2>
          <p style={styles.cardText}>At The Sustainable Formula, we believe in a future where sustainability is at the heart of everything we do. Our mission is to inspire and empower individuals and businesses to embrace eco-friendly practices and make a lasting impact on our planet.</p>
        </section>

        <section 
          style={styles.sectionCard}
          onMouseEnter={(e) => {
            if (windowWidth > 768) {
              e.currentTarget.style.transform = 'translateZ(50px)';
              e.currentTarget.style.boxShadow = isDarkMode ? 
                '20px 20px 60px rgba(46, 204, 113, 0.5), -20px -20px 60px rgba(46, 204, 113, 0.5)' : 
                '20px 20px 60px rgba(94, 171, 112, 0.3), -20px -20px 60px rgba(94, 171, 112, 0.3)';
            }
          }}
          onMouseLeave={(e) => {
            if (windowWidth > 768) {
              e.currentTarget.style.transform = 'translateZ(0)';
              e.currentTarget.style.boxShadow = 'none';
            }
          }}
          onTouchStart={(e) => {
            if (windowWidth <= 768) {
              e.currentTarget.style.transform = 'translateY(-5px)';
              e.currentTarget.style.boxShadow = isDarkMode ? 
                '10px 10px 30px rgba(46, 204, 113, 0.3), -10px -10px 30px rgba(46, 204, 113, 0.3)' : 
                '10px 10px 30px rgba(94, 171, 112, 0.2), -10px -10px 30px rgba(94, 171, 112, 0.2)';
            }
          }}
          onTouchEnd={(e) => {
            if (windowWidth <= 768) {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }
          }}
        >
          <h2 style={styles.cardTitle}>What We Do</h2>
          <p style={styles.cardText}>We develop and promote sustainable products that minimize environmental harm, provide education on green practices, and partner with organizations to further our collective goals. Through innovation and collaboration, we aim to lead the charge in environmental stewardship.</p>
        </section>
      </div>

      {/* Add this CSS for mobile responsiveness */}
      <style>
        {`
          @media (max-width: 768px) {
            .nav-links {
              display: none;
            }
            
            .custom-cursor, .cursor-dot {
              display: none;
            }
            
            .logo_dark, .logo_light {
              font-size: 1rem;
            }
            
            html, body {
              touch-action: manipulation;
              overflow-x: hidden;
            }
            
            .particle {
              opacity: 0.6;
            }

            header h1 {
              font-size: 1.8rem;
            }
          }

          @media (max-width: 480px) {
            .logo_dark, .logo_light {
              font-size: 0.9rem;
              max-width: 200px;
              overflow: hidden;
              text-overflow: ellipsis;
            }

            header h1 {
              font-size: 1.5rem;
            }
          }

          @media (max-width: 360px) {
            nav {
              padding: 8px !important;
            }
          }
        `}
      </style>
    </div>
  );
};

export default HomePage;